import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Hook to check if warning should be shown for unallocated tasks
 * Only shows warning when in sprint filter mode with unallocated tasks
 */
export const useUnallocatedTasksWarning = () => {
  const { capacityPlanningData, capacityPlanningFilters, resourcePageState } = useSelector((state: any) => state.resources);

  const shouldShowWarning = useMemo(() => {
    // Check if we are in Capacity view. If not (e.g. People & Teams), do not show warning.
    const currentView = resourcePageState?.currentView;
    if (currentView !== 'capacity') {
      return false;
    }

    // Only show warning in sprint filter mode
    const searchType = capacityPlanningFilters?.searchType;
    if (searchType !== 'sprint') {
      return false;
    }
    // Only show warning when teams are selected (same condition as drawer visibility)
    const selectedTeams = capacityPlanningFilters?.selectedTeams || [];
    if (!selectedTeams || selectedTeams.length === 0) {
      return false;
    }
    // Check if there are unallocated tasks
    const unallocatedTasks = capacityPlanningData?.unallocated_task || [];
    return unallocatedTasks.length > 0;
  }, [capacityPlanningFilters?.searchType, capacityPlanningFilters?.selectedTeams, capacityPlanningData?.unallocated_task, resourcePageState?.currentView]);

  const unallocatedTasksCount = useMemo(() => {
    return (capacityPlanningData?.unallocated_task || []).length;
  }, [capacityPlanningData?.unallocated_task]);

  const totalStoryPoints = useMemo(() => {
    const unallocatedTasks = capacityPlanningData?.unallocated_task || [];
    return unallocatedTasks.reduce((total: number, task: any) => {
      // Only count story points for user story, bug, and spike types
      const itemType = task?.integration_item_type?.toLowerCase() || '';
      if (!['user story', 'bug', 'spike'].includes(itemType)) {
        return total;
      }
      const storyPoints = task?.story_points;
      if (!storyPoints || storyPoints === '0' || storyPoints === 0) {
        return total;
      }
      const points = typeof storyPoints === 'string' ? parseFloat(storyPoints) : storyPoints;
      return total + (isNaN(points) ? 0 : points);
    }, 0);
  }, [capacityPlanningData?.unallocated_task]);

  const totalOriginalEstimate = useMemo(() => {
    const unallocatedTasks = capacityPlanningData?.unallocated_task || [];
    return unallocatedTasks.reduce((total: number, task: any) => {
      // Only count original estimate for task types
      const itemType = task?.integration_item_type?.toLowerCase() || '';
      if (itemType !== 'task') {
        return total;
      }
      const originalEstimate = task?.original_estimate;
      if (!originalEstimate || originalEstimate === '0' || originalEstimate === 0) {
        return total;
      }
      const estimate = typeof originalEstimate === 'string' ? parseFloat(originalEstimate) : originalEstimate;
      return total + (isNaN(estimate) ? 0 : estimate);
    }, 0);
  }, [capacityPlanningData?.unallocated_task]);

  return {
    shouldShowWarning,
    unallocatedTasksCount,
    totalStoryPoints,
    totalOriginalEstimate,
  };
};